import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Brain } from "lucide-react";

/**
 * Small owner-cockpit chips beneath the welcome banner:
 *   Execution Validation ✓ N/2     — 2 consecutive clean canonical runs
 *   AI Validation: <status>          — Collecting Evidence / Active
 *
 * Deliberately understated. `Execution Validation ✓ 2/2` never implies
 * autonomy is safe — the AI chip lives right next to it precisely to
 * make that distinction obvious.
 */
export default function ValidationBadges() {
  const [runs, setRuns] = useState({ clean: 0, target: 2 });
  const [ai, setAi] = useState({ status: "COLLECTING_EVIDENCE", matured: 0 });
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // Historical best consecutive PASS streak (capped at target).
        // A milestone should not silently regress when the operator
        // runs more experiments after it is achieved.
        const r = await api.get(
          "/harness/supervised-paper-test/clean-runs-milestone",
          { params: { target: 2 }});
        setRuns({
          clean: r.data?.clean ?? 0,
          target: r.data?.target ?? 2,
          achieved: !!r.data?.achieved,
        });
      } catch (_) { /* ignore */ }
      try {
        const s = await api.get("/ai-quality/status");
        setAi({
          status: s.data?.collection_status || "COLLECTING_EVIDENCE",
          matured: s.data?.outcomes_matured || 0,
        });
      } catch (_) { /* ignore */ }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return null;

  const clean = runs.clean;
  const execTone = clean >= 2 ? "text-signal-green border-signal-green/40"
                                       : "text-signal-yellow border-signal-yellow/40";
  const aiActive = ai.status === "ACTIVE";
  const aiTone = aiActive ? "text-signal-green border-signal-green/40"
                                    : "text-signal-yellow border-signal-yellow/40";

  return (
    <div className="flex items-center flex-wrap gap-2" data-testid="validation-badges">
      <button
        onClick={() => nav("/system")}
        className={`chip flex items-center gap-1 !text-[10px] ${execTone}`}
        data-testid="badge-execution-validation"
        title="Two consecutive supervised clean-run round trips required. Click for run reports."
      >
        <CheckCircle2 size={10}/>
        Execution Validation {clean >= 2 ? "✓" : ""} {clean}/{runs.target}
      </button>
      <button
        onClick={() => nav("/ai-quality")}
        className={`chip flex items-center gap-1 !text-[10px] ${aiTone}`}
        data-testid="badge-ai-validation"
        title="AI Recommendation Quality — measured over T+20 matured evaluations. Click to open the scoreboard."
      >
        <Brain size={10}/>
        AI Validation: {aiActive ? "Active" : "Collecting Evidence"} · {ai.matured} matured
      </button>
    </div>
  );
}
